// frontend/src/components/ConfirmDialog.jsx
import React from "react";
import PropTypes from "prop-types";

/**
 * ConfirmDialog — generic confirmation modal.
 *
 * Renders a centered overlay with a title, message, and confirm/cancel buttons.
 * Pass `dangerous={true}` to colour the confirm button red.
 *
 * @param {object}   props
 * @param {string}   props.title       Modal heading
 * @param {string}   props.message     Body text
 * @param {string}   [props.confirmLabel]  Label for the confirm button (default "Confirm")
 * @param {boolean}  [props.dangerous]     Red confirm button when true
 * @param {boolean}  [props.loading]       Disables buttons while an async action runs
 * @param {Function} props.onConfirm    Called when user clicks confirm
 * @param {Function} props.onCancel     Called when user clicks cancel or backdrop
 */
function ConfirmDialog({ title, message, confirmLabel, dangerous, loading, onConfirm, onCancel }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-base font-semibold text-gray-900 mb-2">{title}</h2>
        <p className="text-sm text-gray-600 mb-6">{message}</p>

        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`px-4 py-2 text-sm rounded-lg font-medium text-white disabled:opacity-50 ${
              dangerous
                ? "bg-red-600 hover:bg-red-700"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
          >
            {loading ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

ConfirmDialog.propTypes = {
  title: PropTypes.string.isRequired,
  message: PropTypes.string.isRequired,
  confirmLabel: PropTypes.string,
  dangerous: PropTypes.bool,
  loading: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

ConfirmDialog.defaultProps = {
  confirmLabel: "Confirm",
  dangerous: false,
  loading: false,
};

export default ConfirmDialog;
